import { PostMetric } from 'schemas/PostMetricSchema'
import WidgetType, { WidgetTypeStrings } from 'enums/WidgetType'
import { StreamSymbolStrings } from 'enums/StreamSymbol'
import MetricSeries, { MetricSeriesStrings } from 'enums/MetricSeries'
import { ProsperoPostTypeStrings } from 'enums/ProsperoPostType'
import { TimeZoneStrings } from 'enums/TimeZone'
import { Metadata } from './metadata'

const thousandFormat = Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0
})

const getMetricValueByMetricSeriesType = (
  postMetric: PostMetric,
  seriesName: MetricSeriesStrings
) => {
  const metric = postMetric.dataset!.find((data) => data.seriesName === seriesName)
  if (!metric) {
    return undefined
  }

  return metric!.set![0]
}

const getMetricValueByMetricSeriesTypes = (
  postMetric: PostMetric,
  widgetType: WidgetTypeStrings,
  seriesNames: MetricSeriesStrings[]
) => {
  let value: number | undefined
  seriesNames.forEach((seriesName) => {
    value = value || getMetricValueByMetricSeriesType(postMetric, seriesName)
  })

  if (value !== undefined) {
    return thousandFormat.format(value)
  }

  if (widgetType === WidgetType.SOCIAL_WEB_POPULAR_POSTS) {
    return '--'
  }

  return ''
}

export class PostMetricMetadataAdaptor implements Metadata {
  constructor(
    private postMetric: PostMetric,
    private timeZone: TimeZoneStrings,
    private widgetType: WidgetTypeStrings,
    private vocDisplay?: boolean,
    private needTruncated?: boolean,
    private truncatedLength?: number
  ) {}

  getTimeZone = () => this.timeZone

  isVocDisplay = () => this.vocDisplay

  isNeedTruncated = () => this.needTruncated

  getTruncatedLength = () => this.truncatedLength

  getWidgetType = () => this.widgetType

  getAuthor = () => this.postMetric.author

  getAuthorId = () => this.postMetric.authorID

  getAuthorKarma = () => undefined

  getAuthorLink = () => undefined

  getAuthorLocation = () => undefined

  getAuthorName = () => this.postMetric.authorName || ''

  getAuthorUrl = () => this.postMetric.authorUrl

  getAuthorHandle = () => this.postMetric.authorHandle

  getAvatarUrl = () => this.postMetric.picture

  getOriginalAuthorAvatarUrl = () => this.postMetric.originalAuthorAvatarUrl

  getOriginalAuthorHandle = () => this.postMetric.originalAuthorHandle

  getDateTime = () => this.postMetric.postDate

  getDisplayTextRange = () => undefined

  getDocumentId = () => undefined

  getDomain = () => this.postMetric.domain

  getHighlightingInfos = () => undefined

  isDocumentMode = () => undefined

  getOriginalAuthor = () => this.postMetric.originalAuthor

  getOriginalAuthorName = () => this.postMetric.originalAuthorName

  getOriginalAuthorUrl = () => this.postMetric.originalAuthorUrl

  getPostType = () => this.postMetric.postType as ProsperoPostTypeStrings

  getPostId = () => this.postMetric.postId!

  getSentences = () => this.postMetric.sentences as string[]

  getSentenceInfos = () => undefined

  getSoundbiteSentiment = () => undefined

  getFavicon = () => this.postMetric.favicon

  getUrl = () => this.postMetric.link

  getMentionedUrls = () => this.postMetric.mentionedUrls

  getAuthorFollower = () => undefined

  getAuthorGender = () => undefined

  getAuthorGeoInfos = () => undefined

  isTweet = () => (this.postMetric.tweet ? this.postMetric.tweet : false)

  getDataStreams = () => this.postMetric.dataStreams as StreamSymbolStrings[] | undefined

  getAnnotatedSentiment = () => undefined

  isDeleted = () => undefined

  isSimilar = () => ''

  getDocumentMetaTags = () => []

  getSentenceAllTags = () => undefined

  getSentenceUserTags = () => undefined

  getSentenceMetaTags = () => this.postMetric.sentenceMetaTags as string[][]

  getUserTags = () => undefined

  getMetaTags = () => undefined

  getTitle = () => this.postMetric.title

  isAuthorVerified = () => this.postMetric.authorVerified

  isOriginalAuthorVerified = () => this.postMetric.originalAuthorVerified

  getSourceType = () => this.postMetric.sourceType

  getSourceName = () => this.postMetric.sourceName

  getHitSentence = () => undefined

  getProfessions = () => []

  getInterests = () => []

  getEngagement = () =>
    getMetricValueByMetricSeriesTypes(this.postMetric, this.widgetType, [
      MetricSeries.TOTAL_ENGAGEMENTS
    ])

  getReplies = () =>
    getMetricValueByMetricSeriesTypes(this.postMetric, this.widgetType, [
      MetricSeries.TOTAL_REPLIES
    ])

  getComments = () => this.getReplies()

  getImpressions = () =>
    getMetricValueByMetricSeriesTypes(this.postMetric, this.widgetType, [
      MetricSeries.POST_IMPRESSIONS,
      MetricSeries.IMPRESSIONS
    ])

  getShares = () =>
    getMetricValueByMetricSeriesTypes(this.postMetric, this.widgetType, [MetricSeries.TOP_LINKS])

  getNegSentiment = () =>
    getMetricValueByMetricSeriesTypes(this.postMetric, this.widgetType, [
      MetricSeries.NEGATIVE_SENTIMENT
    ])

  getPassion = () =>
    getMetricValueByMetricSeriesTypes(this.postMetric, this.widgetType, [MetricSeries.PASSION])

  getPosSentiment = () =>
    getMetricValueByMetricSeriesTypes(this.postMetric, this.widgetType, [
      MetricSeries.POSITIVE_SENTIMENT
    ])

  getRetweets = () =>
    getMetricValueByMetricSeriesTypes(this.postMetric, this.widgetType, [
      MetricSeries.TOTAL_REPOSTS
    ])

  getReblogs = () =>
    getMetricValueByMetricSeriesTypes(this.postMetric, this.widgetType, [
      MetricSeries.TOTAL_REPOSTS
    ])

  getSentiments = () =>
    `${getMetricValueByMetricSeriesTypes(this.postMetric, this.widgetType, [
      MetricSeries.NET_SENTIMENT
    ])}%`

  getLikes = () =>
    getMetricValueByMetricSeriesTypes(this.postMetric, this.widgetType, [MetricSeries.LIKES])

  getViews = () =>
    getMetricValueByMetricSeriesTypes(this.postMetric, this.widgetType, [MetricSeries.VIEWS])

  getScore = () =>
    getMetricValueByMetricSeriesTypes(this.postMetric, this.widgetType, [MetricSeries.SCORE])

  getProductName = () => undefined

  getDocHierarchy = () => undefined

  getPostRating = () => undefined

  getPhotoUserTags = () => this.postMetric.photoUserTags as string[] | undefined

  getAdsType = () => this.postMetric.adsType

  getPersistId = () => undefined

  getOverallStarRating = () => this.postMetric.overallStarRating

  getOverallSurveyScore = () => this.postMetric.overallSurveyScore

  getNetPromoterScore = () => this.postMetric.netPromoterScore

  getMeasurements = () => this.postMetric.measurements

  getSentenceMeasurements = () => this.postMetric.sentenceMeasurements

  getParentDistilledTexts = () => this.postMetric.parentDistilledTexts as string[]

  getParentDisplayTextRange = () => this.postMetric.parentDisplayTextRange

  getParentMentionedUrls = () => this.postMetric.parentMentionedUrls

  getFingerprint = () => undefined

  getSentenceNumber = () => undefined
}

export default PostMetricMetadataAdaptor
